/**
 * Subir la boleta o factura de un pedido pagado (DOC-01), dentro del modal del
 * pedido.
 *
 * El negocio emite sus documentos a mano en el SII; el detalle de arriba trae
 * todo lo que hace falta para emitirlo sin salir de aca. Subir el PDF cierra la
 * compra y se lo manda por correo al comprador.
 *
 * Un documento ya subido se puede reemplazar (un RUT mal escrito, una nota de
 * credito): el archivo nuevo pisa al anterior y sale otro correo.
 */
import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import type { Pedido } from '@bandu/compartido';

import { subirDocumentoPedido } from '../../servicios/administracion.servicio';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';

export interface FormularioDocumentoPedidoProps {
  pedido: Pick<Pedido, 'id' | 'tipoDocumento' | 'folioDocumento' | 'contacto'>;
  onSubido(pedido: Pedido): void;
}

export function FormularioDocumentoPedido({ pedido, onSubido }: FormularioDocumentoPedidoProps) {
  const id = useId();
  const [folio, setFolio] = useState(pedido.folioDocumento ?? '');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  const nombre = pedido.tipoDocumento === 'factura' ? 'factura' : 'boleta';

  async function enviar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    if (folio.trim().length === 0 || archivo === null) {
      setFalla(`Escribe el folio y elige el PDF de la ${nombre}.`);
      return;
    }

    setEnviando(true);
    setFalla(null);
    try {
      onSubido(await subirDocumentoPedido(pedido.id, folio.trim(), archivo));
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form
      className="formulario-admin"
      onSubmit={(evento) => {
        void enviar(evento);
      }}
      noValidate
    >
      {falla !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{falla}</span>
        </p>
      ) : null}

      <div className="campos-dos">
        <div className="campo">
          <label htmlFor={`${id}-folio`}>Folio de la {nombre}</label>
          <input
            id={`${id}-folio`}
            className="mono"
            inputMode="numeric"
            value={folio}
            onChange={(evento) => {
              setFolio(evento.target.value);
            }}
            maxLength={40}
            required
          />
        </div>
        <div className="campo">
          <label htmlFor={`${id}-archivo`}>PDF emitido en el SII</label>
          <input
            id={`${id}-archivo`}
            type="file"
            accept="application/pdf"
            onChange={(evento) => {
              setArchivo(evento.target.files?.[0] ?? null);
            }}
            required
          />
        </div>
      </div>

      <p className="parrafo-panel">Al subirla se la mandamos por correo a {pedido.contacto.email}.</p>

      <div className="modal-acciones">
        <button type="submit" className="btn btn-primario" disabled={enviando}>
          {enviando
            ? 'Subiendo…'
            : pedido.folioDocumento !== null
              ? `Reemplazar la ${nombre}`
              : `Subir la ${nombre}`}
        </button>
      </div>
    </form>
  );
}
